import { useState, useEffect } from 'react';
import { useUpgrade } from '../hooks/useUpgrade';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';

/**
 * PricingPage - Standalone pricing page that opens in browser
 *
 * Uses LemonSqueezy for:
 * - Checkout page with tax calculations
 * - Secure payment processing
 * - Localized pricing per customer location
 */

export function PricingPage() {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('yearly');
  const { upgradeToPro, isLoading } = useUpgrade();
  const { user, authState } = useAuth();
  const [authenticationAttempted, setAuthenticationAttempted] = useState(false);

  // Ensure body can scroll and reset all scrolling constraints
  useEffect(() => {
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    document.body.style.minHeight = '100vh';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.body.style.minHeight = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
    };
  }, []);

  // Authenticate from URL tokens
  useEffect(() => {
    const authenticateFromToken = async () => {
      console.log('[PricingPage] Full URL:', window.location.href);
      console.log('[PricingPage] Search params:', window.location.search);

      const params = new URLSearchParams(window.location.search);
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');

      console.log('[PricingPage] URL params:', {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        accessTokenLength: accessToken?.length || 0,
        refreshTokenLength: refreshToken?.length || 0
      });

      if (accessToken && refreshToken) {
        console.log('[PricingPage] Found tokens in URL, authenticating...');

        try {
          // Set the session using both tokens
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) {
            console.error('[PricingPage] Auth error:', error);
          } else {
            console.log('[PricingPage] Successfully authenticated:', data.user?.email);
          }
        } catch (error) {
          console.error('[PricingPage] Failed to authenticate:', error);
        }

        // Remove tokens from URL for security
        window.history.replaceState(null, '', window.location.pathname);
        console.log('[PricingPage] Tokens removed from URL');
      } else {
        console.log('[PricingPage] No tokens in URL, checking existing session...');
      }

      setAuthenticationAttempted(true);
    };

    authenticateFromToken();
  }, []);

  const handleSubscribe = (plan: 'monthly' | 'yearly') => {
    upgradeToPro(plan);
  };

  // Show loading state while authenticating or checking auth
  if (!authenticationAttempted || authState === 'loading') {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(to bottom right, #F0FDFA, #DBEAFE)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #E5E7EB',
            borderTopColor: '#0D9488',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#6B7280', fontSize: '16px' }}>Loading...</p>
        </div>
      </div>
    );
  }

  // Show sign-in prompt if not authenticated
  if (authState === 'signed-out' || !user) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(to bottom right, #F0FDFA, #DBEAFE)'
      }}>
        <div style={{
          textAlign: 'center',
          backgroundColor: 'white',
          padding: '48px',
          borderRadius: '12px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          maxWidth: '400px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#111827',
            marginBottom: '16px'
          }}>
            Sign In Required
          </h2>
          <p style={{
            fontSize: '16px',
            color: '#6B7280',
            marginBottom: '24px'
          }}>
            Please sign in from the EMDR Sync app to continue
          </p>
          <button
            onClick={() => window.close()}
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: 600,
              color: 'white',
              backgroundColor: '#0D9488',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Close Window
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      height: 'auto',
      background: 'linear-gradient(to bottom right, #F0FDFA, #DBEAFE)',
      padding: '48px 24px',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {/* User Info Banner */}
      <div style={{
        maxWidth: '1000px',
        margin: '0 auto 24px',
        backgroundColor: 'white',
        padding: '12px 24px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px', color: '#6B7280' }}>Signed in as:</span>
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
            {user.email}
          </span>
        </div>
        <span style={{
          fontSize: '12px',
          color: '#10B981',
          backgroundColor: '#D1FAE5',
          padding: '4px 12px',
          borderRadius: '12px',
          fontWeight: 600
        }}>
          ✓ Authenticated
        </span>
      </div>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <h1 style={{
          fontSize: '36px',
          fontWeight: 'bold',
          color: '#111827',
          marginBottom: '16px'
        }}>
          Upgrade to EMDR Sync Pro
        </h1>
        <p style={{
          fontSize: '18px',
          color: '#6B7280',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          Unlock all professional EMDR therapy features
        </p>
      </div>

      {/* Billing Toggle */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '48px'
      }}>
        <div style={{
          display: 'inline-flex',
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '4px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <button
            onClick={() => setBillingPeriod('monthly')}
            style={{
              padding: '12px 32px',
              fontSize: '16px',
              fontWeight: 500,
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              backgroundColor: billingPeriod === 'monthly' ? '#F3F4F6' : 'transparent',
              color: billingPeriod === 'monthly' ? '#111827' : '#6B7280',
              transition: 'all 0.2s'
            }}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingPeriod('yearly')}
            style={{
              padding: '12px 32px',
              fontSize: '16px',
              fontWeight: 500,
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              backgroundColor: billingPeriod === 'yearly' ? '#DBEAFE' : 'transparent',
              color: billingPeriod === 'yearly' ? '#1E40AF' : '#6B7280',
              transition: 'all 0.2s',
              position: 'relative'
            }}
          >
            Annual
            <span style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              fontSize: '11px',
              fontWeight: 600,
              color: '#059669',
              backgroundColor: '#D1FAE5',
              padding: '2px 6px',
              borderRadius: '4px'
            }}>
              Save 17%
            </span>
          </button>
        </div>
      </div>

      {/* Pricing Cards */}
      <div style={{
        maxWidth: '1000px',
        width: '100%',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        justifyContent: 'center',
        boxSizing: 'border-box'
      }}>
        {/* Monthly Plan */}
        {billingPeriod === 'monthly' && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '2px solid #E5E7EB'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '8px',
              textAlign: 'center'
            }}>
              Monthly
            </h2>

            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                fontSize: '48px',
                fontWeight: 'bold',
                color: '#111827'
              }}>
                $15
              </div>
              <div style={{
                fontSize: '16px',
                color: '#6B7280'
              }}>
                per month
              </div>
              <div style={{
                fontSize: '14px',
                color: '#9CA3AF',
                marginTop: '4px'
              }}>
                + applicable taxes
              </div>
            </div>

            <ul style={{
              listStyle: 'none',
              padding: 0,
              marginBottom: '32px',
              fontSize: '15px',
              color: '#374151'
            }}>
              <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#10B981', marginRight: '8px', fontSize: '20px' }}>✓</span>
                All colors & sounds
              </li>
              <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#10B981', marginRight: '8px', fontSize: '20px' }}>✓</span>
                All movement patterns
              </li>
              <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#10B981', marginRight: '8px', fontSize: '20px' }}>✓</span>
                Volume & speed control
              </li>
              <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#10B981', marginRight: '8px', fontSize: '20px' }}>✓</span>
                Mixed mode
              </li>
            </ul>

            <button
              onClick={() => handleSubscribe('monthly')}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '16px',
                fontWeight: 600,
                color: 'white',
                backgroundColor: isLoading ? '#9CA3AF' : '#0D9488',
                border: 'none',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) e.currentTarget.style.backgroundColor = '#0F766E';
              }}
              onMouseLeave={(e) => {
                if (!isLoading) e.currentTarget.style.backgroundColor = '#0D9488';
              }}
            >
              {isLoading ? 'Loading...' : 'Subscribe Monthly'}
            </button>
          </div>
        )}

        {/* Yearly Plan */}
        {billingPeriod === 'yearly' && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            boxShadow: '0 8px 16px -4px rgba(0, 0, 0, 0.15)',
            border: '3px solid #0D9488',
            position: 'relative'
          }}>
            {/* Recommended Badge */}
            <div style={{
              position: 'absolute',
              top: '-16px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: '#0D9488',
              color: 'white',
              padding: '6px 20px',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: 600
            }}>
              Recommended
            </div>

            <h2 style={{
              fontSize: '24px',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '8px',
              textAlign: 'center',
              marginTop: '8px'
            }}>
              Yearly
            </h2>

            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                fontSize: '48px',
                fontWeight: 'bold',
                color: '#111827'
              }}>
                $150
              </div>
              <div style={{
                fontSize: '16px',
                color: '#6B7280'
              }}>
                per year
              </div>
              <div style={{
                fontSize: '14px',
                color: '#9CA3AF',
                marginTop: '4px'
              }}>
                + applicable taxes
              </div>
            </div>

            <ul style={{
              listStyle: 'none',
              padding: 0,
              marginBottom: '32px',
              fontSize: '15px',
              color: '#374151'
            }}>
              <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#10B981', marginRight: '8px', fontSize: '20px' }}>✓</span>
                All colors & sounds
              </li>
              <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#10B981', marginRight: '8px', fontSize: '20px' }}>✓</span>
                All movement patterns
              </li>
              <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#10B981', marginRight: '8px', fontSize: '20px' }}>✓</span>
                Volume & speed control
              </li>
              <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#10B981', marginRight: '8px', fontSize: '20px' }}>✓</span>
                Mixed mode
              </li>
            </ul>

            <button
              onClick={() => handleSubscribe('yearly')}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '16px',
                fontWeight: 600,
                color: 'white',
                backgroundColor: isLoading ? '#9CA3AF' : '#0D9488',
                border: 'none',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) e.currentTarget.style.backgroundColor = '#0F766E';
              }}
              onMouseLeave={(e) => {
                if (!isLoading) e.currentTarget.style.backgroundColor = '#0D9488';
              }}
            >
              {isLoading ? 'Loading...' : 'Subscribe Yearly'}
            </button>
          </div>
        )}
      </div>

      {/* Footer Note */}
      <div style={{
        textAlign: 'center',
        marginTop: '48px',
        color: '#6B7280',
        fontSize: '14px'
      }}>
        <p>✓ Secure payment powered by LemonSqueezy</p>
        <p style={{ marginTop: '8px' }}>✓ Cancel anytime</p>
      </div>
    </div>
  );
}